import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { Store } from '@ngrx/store';
import {  deleteUndefinedEntriesInPlace, removeFromListByIndex } from '../helpers/helpers';
import { Sponsor, sponsorStatus } from 'globals/store/sponsors.data';
import { ProgramService } from './program.service';
import { SessionInfo, SponsorSessionInfo } from '../store/reselectors/performerSponsors.reselector';
import { Ranking } from 'globals/store/events.data';
import { EntitiesService } from './entities.service';
import { createId, jsonCopyObject } from 'globals/helpers/helpers';

// separating this out into its own service for better separation of concerns / clarity between services
@Injectable({ providedIn: 'root' })
export class SponsorsService {

  constructor(
    private programService: ProgramService,
    private eventService: EventService,
    private entitiesService: EntitiesService,
    public store: Store,
  ) {}
  
  getSponsorBase = (partialSponsor:Partial<Sponsor>):Sponsor=>({
    id: createId(),
    social: [],
    labels: [],
    name: '',
    ...jsonCopyObject(partialSponsor)
  })

  async addSponsor({ name }: {name?: string}): Promise<Sponsor> {
    const sponsor: Sponsor = this.getSponsorBase({name})
    await this.eventService.saveEventPageEntities('sponsors', [sponsor])
    return sponsor
  }

  async addMultipleSponsors(partialSponsors:Partial<Sponsor>[]): Promise<Sponsor[]> {
    const newSponsors = partialSponsors.map(ps=>this.getSponsorBase(ps))
    await this.eventService.saveEventPageEntities('sponsors', newSponsors)
    return newSponsors
  }

  addSponsorToMultipleSessions(sessions: SessionInfo[], sponsorId: string) {
    this.programService.addEntityToMultipleSessions(sessions, sponsorId, 'sponsorIds', 'sponsorStatus', sponsorStatus.Pending)
  }

  addSponsorsToProgram(session: SessionInfo, sponsorIds: string[]) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      sponsorIds.forEach(sponsorId => {
        program.sessions_obj[id].sponsorIds.push(sponsorId)
        program.sessions_obj[id].sponsorStatus[sponsorId] = sponsorStatus.Pending
      })
      this.programService.upsertProgram(program)
    })
  }


  deleteSponsorFromSession(session: SponsorSessionInfo | SessionInfo, sponsorId: string) {
    const { programId, id } = session
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      const { sponsorStatus, sponsorIds } = program.sessions_obj[id]
      delete sponsorStatus[sponsorId]
      removeFromListByIndex(sponsorIds, sponsorIds.findIndex(i => sponsorId === i))
      this.programService.upsertProgram(program)
    })
  }

  changeSponsorStatus(sessionId: string, programId: string, status: string, sponsorId: string) {
    this.programService.getProgramPromise(programId).then(passedProgram => {
      const program = jsonCopyObject(passedProgram);
      program.sessions_obj[sessionId].sponsorStatus[sponsorId] = status
      this.programService.upsertProgram(program)
    })
  }

  async updateSponsorTypes(sponsorTypes: Ranking[]) {
    sponsorTypes.forEach((level, i) => level.rank = sponsorTypes.length - i)
    const event = await this.eventService.getCurrentEvent()
    this.eventService.updateEvent(event.eventId, { sponsorTypes })
  }

  async updateSponsors(updatedSponsors: Sponsor[]) {
    updatedSponsors.forEach(deleteUndefinedEntriesInPlace)
    await this.eventService.saveEventPageEntities('sponsors', updatedSponsors)
    return updatedSponsors
  }

  updateSponsor(sponsor: Sponsor) {
    this.updateSponsors([sponsor])
  }

  async templateSponsors(sponsors: Sponsor[]) {
    const sponsorTemplates = await this.entitiesService.createEntityTemplates('sponsor', sponsors)
    const updatedSponsors = sponsors.map((s, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = sponsorTemplates[i]
      const updatedSponsor: Sponsor = { ...s, dateAdded, dateModified, templateId, versionNumber }
      return updatedSponsor
    })
    await this.updateSponsors(updatedSponsors)
  }

  async deleteSponsor(sponsor: Sponsor) {
    await this.deleteSponsors([sponsor])
  }

  async deleteSponsors(sponsors: Sponsor[]) {
    await this.eventService.deleteFromEventPages('sponsors', sponsors.map(s => s.id))
  }

}